import { useState, useEffect, useCallback, useMemo } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  ShieldCheck, ShieldAlert, Clock, Building2, User, RefreshCw, AlertTriangle,
} from "lucide-react";
import api, { formatDateTime } from "@/lib/api";
import { toast } from "sonner";
import { usePermission } from "@/hooks/usePermission";

const REASON_MIN = 20;

const STATUS_BADGE = {
  in_review: { classes: "border-yellow-500/40 text-yellow-400 bg-yellow-500/10", label: "Em análise" },
  pending: { classes: "border-slate-500/40 text-slate-400 bg-slate-500/10", label: "Não iniciado" },
};

const diasDesde = (iso) => {
  if (!iso) return null;
  const dias = Math.floor((Date.now() - new Date(iso).getTime()) / 86400000);
  return dias < 0 ? 0 : dias;
};

/**
 * Toda recusa da API tem mensagem PRÓPRIA. Um "erro ao aprovar" genérico aqui
 * seria pior que inútil: 409 de KYC já recusado pela Trustic, 409 de chamado
 * divergente e 403 de registro sem verificação vinculada pedem AÇÕES
 * diferentes de quem está na tela — e só o 429 é caso de simplesmente esperar.
 */
const mensagemDeErro = (err) => {
  if (!err?.response) {
    return "Sem resposta da API. Verifique a conexão e tente de novo.";
  }
  const { status, data } = err.response;
  const detalhe = typeof data?.detail === "string" ? data.detail : "";
  if (status === 429) {
    return "Limite de 10 aprovações por minuto atingido. Aguarde um minuto antes de continuar.";
  }
  if (status === 403) {
    return detalhe || "Sem permissão para aprovar KYC manualmente.";
  }
  if (status === 404) {
    return detalhe || "Registro de KYC não encontrado — a lista pode estar desatualizada.";
  }
  if (status === 409) {
    return detalhe || "O registro mudou de estado e não pode mais ser aprovado.";
  }
  if (status === 400) {
    return detalhe || "Dados da aprovação inválidos.";
  }
  return detalhe || "Não foi possível aprovar. Tente novamente em instantes.";
};

export default function AdminKycPending() {
  const { canEdit } = usePermission("aprovacoes");
  const [itens, setItens] = useState([]);
  const [fila, setFila] = useState({ total: 0, truncated: false });
  const [carregando, setCarregando] = useState(true);
  const [erroCarga, setErroCarga] = useState(null);
  const [alvo, setAlvo] = useState(null);
  const [motivo, setMotivo] = useState("");
  const [chamado, setChamado] = useState("");
  const [confirmado, setConfirmado] = useState(false);
  const [enviando, setEnviando] = useState(false);

  const buscar = useCallback(async () => {
    setCarregando(true);
    try {
      const res = await api.get("/admin/kyc/pending");
      const items = res.data.items || [];
      setItens(items);
      // `total` é a fila inteira; a lista pode ser só a primeira página.
      setFila({ total: res.data.total ?? items.length, truncated: !!res.data.truncated });
      setErroCarga(null);
    } catch (err) {
      setErroCarga(mensagemDeErro(err));
    }
    setCarregando(false);
  }, []);

  useEffect(() => { buscar(); }, [buscar]);

  const fecharModal = () => {
    setAlvo(null); setMotivo(""); setChamado(""); setConfirmado(false);
  };

  const motivoOk = motivo.trim().length >= REASON_MIN;
  const chamadoOk = chamado.trim().length >= 3;
  const podeEnviar = motivoOk && chamadoOk && confirmado && !enviando;

  const aprovar = async () => {
    if (!alvo || !podeEnviar) return;
    setEnviando(true);
    try {
      const res = await api.post(`/admin/kyc/${alvo.id}/approve`, {
        employee_id: alvo.employee_id,
        reason: motivo.trim(),
        ticket_ref: chamado.trim(),
      });
      if (res.data.already_approved) {
        toast.info(`KYC de ${alvo.employee_name} já estava aprovado por este mesmo chamado`);
      } else {
        toast.success(`KYC de ${alvo.employee_name} aprovado manualmente`);
      }
      fecharModal();
    } catch (err) {
      // A recusa não fecha o modal: o operador precisa ler o motivo e decidir
      // se corrige o chamado ou desiste.
      toast.error(mensagemDeErro(err));
    }
    setEnviando(false);
    // Em SUCESSO ou em RECUSA a fila pode ter mudado (foi outro operador que
    // aprovou, o webhook chegou atrasado...). Reler é sempre a leitura honesta.
    buscar();
  };

  const aprovaveis = useMemo(() => itens.filter((i) => i.aprovavel).length, [itens]);

  if (carregando) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="kyc-pending-loading">
        <div className="loading-spinner w-8 h-8" />
      </div>
    );
  }

  return (
    <div className="p-4 pt-16 lg:pt-4 lg:p-8 space-y-6" data-testid="admin-kyc-pending">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-xl lg:text-2xl font-bold tracking-tight flex items-center gap-2">
            <ShieldAlert className="w-5 h-5 text-yellow-400" /> KYC Pendentes
          </h1>
          <p className="text-sm text-muted-foreground mt-1" data-testid="kyc-pending-count">
            {fila.total} colaborador{fila.total !== 1 ? "es" : ""} travado{fila.total !== 1 ? "s" : ""}
            {itens.length > 0 && ` · ${aprovaveis} ${aprovaveis !== 1 ? "aprováveis" : "aprovável"} manualmente`}
            {/* A contagem é da fila inteira; a lista pode ser a primeira
                página. Dizer só "N travados" com N linhas na tela faria o
                operador achar que está vendo tudo. */}
            {fila.truncated && ` · mostrando ${itens.length} (aprováveis primeiro)`}
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={buscar} className="border-white/10"
          data-testid="kyc-pending-refresh">
          <RefreshCw className="w-4 h-4 mr-2" /> Atualizar
        </Button>
      </div>

      <Card className="border-blue-500/20 bg-blue-500/5">
        <CardContent className="p-4 text-sm text-muted-foreground">
          A aprovação manual destrava quem <strong className="text-foreground">já se identificou
          na Trustic</strong> e ficou sem retorno do provedor. Ela não substitui a verificação:
          quem ainda não se identificou, ou teve KYC recusado, não aparece como aprovável.
          Toda aprovação fica registrada com autor, motivo e chamado na trilha de auditoria.
        </CardContent>
      </Card>

      {erroCarga ? (
        <Card className="border-red-500/20 bg-red-500/5" data-testid="kyc-pending-error">
          <CardContent className="p-8 text-center space-y-4">
            <AlertTriangle className="w-8 h-8 text-red-400 mx-auto" />
            <p className="text-sm text-red-400">{erroCarga}</p>
            <Button variant="outline" onClick={buscar} className="border-white/10">
              Tentar de novo
            </Button>
          </CardContent>
        </Card>
      ) : itens.length === 0 ? (
        <Card className="border-white/5" data-testid="kyc-pending-empty">
          <CardContent className="p-8 text-center text-muted-foreground">
            Nenhum KYC travado no momento.
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {itens.map((item) => {
            const badge = STATUS_BADGE[item.status] || STATUS_BADGE.pending;
            const dias = diasDesde(item.created_at);
            return (
              <Card key={item.id} className="border-white/5" data-testid={`kyc-row-${item.id}`}>
                <CardContent className="p-5">
                  <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                    <div className="space-y-2 flex-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <User className="w-4 h-4 text-blue-400 shrink-0" />
                        <p className="font-semibold truncate">{item.employee_name}</p>
                        <Badge variant="outline" className={badge.classes}>{badge.label}</Badge>
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm text-muted-foreground">
                        <div className="flex items-center gap-2 truncate">
                          <Building2 className="w-3.5 h-3.5 shrink-0" />
                          <span className="truncate">{item.company_name}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <Clock className="w-3.5 h-3.5 shrink-0" />
                          Desde {formatDateTime(item.created_at)}
                          {dias != null && ` (${dias} dia${dias !== 1 ? "s" : ""})`}
                        </div>
                      </div>
                      {!item.aprovavel && (
                        <p className="text-xs text-yellow-400/80" data-testid={`kyc-blocked-${item.id}`}>
                          {item.motivo_bloqueio}
                        </p>
                      )}
                    </div>
                    <div className="shrink-0">
                      <Button
                        data-testid={`kyc-approve-btn-${item.id}`}
                        onClick={() => { fecharModal(); setAlvo(item); }}
                        disabled={!canEdit || !item.aprovavel}
                        title={!canEdit ? "Sem permissão para aprovar"
                          : !item.aprovavel ? item.motivo_bloqueio : undefined}
                        className="bg-emerald-600 hover:bg-emerald-700 text-white font-semibold">
                        <ShieldCheck className="w-4 h-4 mr-2" /> Aprovar manualmente
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      <Dialog open={!!alvo} onOpenChange={(aberto) => { if (!aberto) fecharModal(); }}>
        <DialogContent className="sm:max-w-lg border-white/10" data-testid="kyc-approve-dialog">
          <DialogHeader>
            <DialogTitle>Aprovar KYC manualmente</DialogTitle>
            <DialogDescription>
              {alvo && (
                <>
                  <strong className="text-foreground">{alvo.employee_name}</strong> · {alvo.company_name}.
                  A aprovação libera a antecipação para este colaborador e fica registrada em
                  seu nome na trilha de auditoria.
                </>
              )}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label htmlFor="kyc-ticket">
                Chamado / ticket <span className="text-red-400">*</span>
              </Label>
              <Input id="kyc-ticket" data-testid="kyc-ticket-input" value={chamado}
                onChange={(e) => setChamado(e.target.value)} maxLength={64}
                placeholder="Ex.: TCK-1042" className="bg-black/20 border-white/10" />
            </div>

            <div className="space-y-2">
              <Label htmlFor="kyc-reason">
                Justificativa <span className="text-red-400">*</span>
              </Label>
              <Textarea id="kyc-reason" data-testid="kyc-reason-input" value={motivo}
                onChange={(e) => setMotivo(e.target.value)} rows={4} maxLength={500}
                placeholder="O que foi verificado e por que a aprovação manual é necessária."
                className="bg-black/20 border-white/10" />
              <p className={`text-xs ${motivoOk ? "text-muted-foreground" : "text-yellow-400"}`}>
                {motivo.trim().length}/{REASON_MIN} caracteres mínimos
              </p>
            </div>

            <label className="flex items-start gap-3 text-sm cursor-pointer">
              <Checkbox checked={confirmado} onCheckedChange={(v) => setConfirmado(v === true)}
                data-testid="kyc-confirm-checkbox" className="mt-0.5" />
              <span className="text-muted-foreground">
                Confirmo que verifiquei a identificação deste colaborador na Trustic e que a
                aprovação manual é a decisão correta.
              </span>
            </label>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={fecharModal} className="border-white/10">Cancelar</Button>
            <Button data-testid="kyc-confirm-approve-btn" onClick={aprovar} disabled={!podeEnviar}
              className="bg-emerald-600 hover:bg-emerald-700 text-white font-semibold">
              {enviando ? <div className="loading-spinner" /> : "Confirmar aprovação"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
